/**
 * Minimal WHEP (WebRTC-HTTP Egress Protocol) client.
 *
 * MediaMTX exposes WHEP at `http(s)://{host}:{webrtcPort}/{path}/whep`.
 * The flow is a single request/response SDP exchange:
 *   1. Browser creates an RTCPeerConnection (recvonly video+audio).
 *   2. POSTs the offer SDP to the WHEP URL with `Content-Type: application/sdp`.
 *   3. MediaMTX answers with the answer SDP body and a `Location` header for the
 *      WHEP session resource (used to terminate the session via DELETE).
 *   4. Browser sets the answer as the remote description and tracks start flowing.
 */

export interface WhepSession {
  pc: RTCPeerConnection;
  sessionUrl: string | null;
  close: () => void;
}

export interface StartWhepStreamOptions {
  url: string;
  videoEl: HTMLVideoElement;
  signal?: AbortSignal;
}

export interface ResilientWhepPlaybackOptions extends StartWhepStreamOptions {
  /** Total WHEP session (re)connect attempts including the first. */
  maxSessionAttempts?: number;
  /** Wait after ICE `disconnected` before reconnecting (ms). */
  disconnectedGraceMs?: number;
  onReconnecting?: () => void;
  onGiveUp?: () => void;
}

export interface ResilientWhepPlayback {
  close: () => void;
}

const WHEP_REQUEST_ATTEMPTS = 5;
const WHEP_RETRY_DELAY_MS = 600;
const WHEP_SESSION_RECONNECT_DELAY_MS = 900;
const WHEP_DISCONNECTED_GRACE_MS = 2_500;
const DEFAULT_WHEP_SESSION_ATTEMPTS = 6;
const RETRYABLE_WHEP_STATUSES = new Set([404, 409, 503]);

class WhepRequestError extends Error {
  constructor(
    public readonly status: number,
    statusText: string,
  ) {
    super(`WHEP request failed: HTTP ${status} ${statusText}`);
  }
}

export async function startWhepStream({
  url,
  videoEl,
  signal,
}: StartWhepStreamOptions): Promise<WhepSession> {
  const pc = new RTCPeerConnection();
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  pc.ontrack = (event) => {
    if (event.streams[0]) {
      videoEl.srcObject = event.streams[0];
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  let sessionUrl: string | null = null;

  try {
    const res = await postWhepOfferWithRetry(url, offer.sdp ?? '', signal);

    sessionUrl = res.headers.get('location');
    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  } catch (err) {
    pc.close();
    throw err;
  }

  const close = (): void => {
    if (sessionUrl) {
      void fetch(absolutize(sessionUrl, url), { method: 'DELETE', keepalive: true }).catch(
        () => undefined,
      );
    }
    pc.getReceivers().forEach((r) => {
      try {
        r.track?.stop();
      } catch {
        /* ignore */
      }
    });
    pc.close();
  };

  return { pc, sessionUrl, close };
}

/**
 * WHEP playback with automatic session recovery on ICE/connection loss.
 */
export function startResilientWhepPlayback({
  url,
  videoEl,
  signal,
  maxSessionAttempts = DEFAULT_WHEP_SESSION_ATTEMPTS,
  disconnectedGraceMs = WHEP_DISCONNECTED_GRACE_MS,
  onReconnecting,
  onGiveUp,
}: ResilientWhepPlaybackOptions): Promise<ResilientWhepPlayback> {
  let closed = false;
  let activeSession: WhepSession | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let sessionAttempts = 0;

  const clearTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
      disconnectTimer = null;
    }
  };

  const closeActive = () => {
    clearTimers();
    if (activeSession) {
      activeSession.close();
      activeSession = null;
    }
    try {
      videoEl.srcObject = null;
    } catch {
      /* ignore */
    }
  };

  const scheduleReconnect = (reason: "failed" | "disconnected") => {
    if (closed || signal?.aborted) return;
    if (reason === "disconnected") {
      if (disconnectTimer) return;
      disconnectTimer = setTimeout(() => {
        disconnectTimer = null;
        void attemptConnect(true);
      }, disconnectedGraceMs);
      return;
    }
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
      disconnectTimer = null;
    }
    if (reconnectTimer) return;
    const delay =
      WHEP_SESSION_RECONNECT_DELAY_MS +
      Math.min(sessionAttempts, 4) * 400;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void attemptConnect(true);
    }, delay);
  };

  const wirePc = (pc: RTCPeerConnection) => {
    pc.onconnectionstatechange = () => {
      if (closed) return;
      if (pc.connectionState === "connected") {
        if (disconnectTimer) {
          clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }
        return;
      }
      if (pc.connectionState === "failed") {
        scheduleReconnect("failed");
      } else if (pc.connectionState === "disconnected") {
        scheduleReconnect("disconnected");
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (closed) return;
      if (pc.iceConnectionState === "failed") {
        scheduleReconnect("failed");
      } else if (pc.iceConnectionState === "disconnected") {
        scheduleReconnect("disconnected");
      }
    };
  };

  const attemptConnect = async (isReconnect: boolean): Promise<void> => {
    if (closed || signal?.aborted) return;
    if (isReconnect) {
      onReconnecting?.();
      closeActive();
    }
    if (sessionAttempts >= maxSessionAttempts) {
      onGiveUp?.();
      return;
    }
    sessionAttempts += 1;

    try {
      const session = await startWhepStream({ url, videoEl, signal });
      if (closed || signal?.aborted) {
        session.close();
        return;
      }
      activeSession = session;
      wirePc(session.pc);
    } catch {
      if (closed || signal?.aborted) return;
      if (sessionAttempts >= maxSessionAttempts) {
        onGiveUp?.();
        return;
      }
      const delay =
        WHEP_SESSION_RECONNECT_DELAY_MS +
        Math.min(sessionAttempts, 4) * 400;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void attemptConnect(true);
      }, delay);
    }
  };

  return attemptConnect(false).then(() => ({
    close: () => {
      closed = true;
      closeActive();
    },
  }));
}

async function postWhepOfferWithRetry(
  url: string,
  sdp: string,
  signal?: AbortSignal,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= WHEP_REQUEST_ATTEMPTS; attempt += 1) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: sdp,
        signal,
      });

      if (res.ok) return res;

      throw new WhepRequestError(res.status, res.statusText);
    } catch (err) {
      if (signal?.aborted || !isRetryableWhepError(err) || attempt === WHEP_REQUEST_ATTEMPTS) {
        throw err;
      }

      lastError = err;
      await wait(WHEP_RETRY_DELAY_MS, signal);
    }
  }

  throw lastError;
}

function isRetryableWhepError(err: unknown): boolean {
  if (err instanceof WhepRequestError) {
    return RETRYABLE_WHEP_STATUSES.has(err.status);
  }
  return err instanceof TypeError || err instanceof DOMException;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(signal.reason);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, ms);

    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

function absolutize(maybeRelative: string, base: string): string {
  try {
    return new URL(maybeRelative, base).toString();
  } catch {
    return maybeRelative;
  }
}
